// @ts-strict-ignore
import Modal from 'antd/lib/modal/Modal';
import Form from 'antd/lib/form';
import { useForm } from 'antd/lib/form/Form';
import * as React from 'react';
import {
    AppMonitoringHealthMetric,
    AppMonitoringHealthMetricCategory,
    AppMonitoringHealthMetricType,
    AppMonitoringObservabilityAndAnalyticsHealthMetric,
    AutomationHaltUnstableRelease,
    AutomationType,
    Integration,
    MetricConditionOperator,
} from '@/types';
import { DeltaMetricOperator, ExtendedStatsByType } from '@/typescriptHelpers';
import { AppSettingsUpdatable } from '@/actions/updatable';
import {
    eventIdFromEventAndParam,
    isMetricTypeComposed,
    requiredCapabilityForMonitoringMetricCategory,
    separateEventAndParam,
} from '@/helpers';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectAutomationsByType,
    selectIntegrationsForCapability,
    selectIntegrationsFromIntegrationInstallationIds,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { flatMap } from 'lodash';
import {
    AddMonitoringMetricRequest,
    AppMonitoringHealthMetricThreshold,
    tryUpdateAppSettings,
    UpdateMonitoringMetricRequest,
} from '@/actions/appSettings';
import { globalMessage } from '@/utils/globalMessage';
import { isDeltaMetric, MonitoringMetricFormFields, MonitoringMetricFormValues } from './MonitoringMetricFormFields';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const generateInitialValues = ({
    metric,
    relevantIntegrations,
}: {
    metric?: AppMonitoringHealthMetric;
    relevantIntegrations: Integration[];
}): MonitoringMetricFormValues => {
    const isComposed = isMetricTypeComposed(metric?.type);
    const onlyPossibleIntegrationInstallationId =
        relevantIntegrations?.length === 1 ? relevantIntegrations[0].installationId : null;

    return {
        isComposedMetric: isComposed,
        selectedIntegrationInstallationId:
            isComposed ? null : (metric?.integrationId ?? onlyPossibleIntegrationInstallationId),
        selectedMetricType: metric?.type,
        selectedEventId:
            metric ? eventIdFromEventAndParam(metric as AppMonitoringObservabilityAndAnalyticsHealthMetric) : null,
        thresholds: metric?.thresholds.map(mapThresholdToFormValue),
        composedMetrics: (metric as AppMonitoringObservabilityAndAnalyticsHealthMetric)?.composedMetricIds?.map(
            (metricId) => {
                return { id: metricId };
            },
        ),
        composeMetricsUsingEquation: (metric as AppMonitoringObservabilityAndAnalyticsHealthMetric)
            ?.composeMetricsUsingEquation,
        eventName: (metric as AppMonitoringObservabilityAndAnalyticsHealthMetric)?.eventName,
        hideOnRolloutView: metric?.hideOnRolloutView ?? false,
        notifyOnApproachingThreshold: metric?.notifyOnApproachingThreshold ?? false,
        metricDescription: metric?.description,
    };
};

export const mapThresholdToFormValue = (threshold: AppMonitoringHealthMetricThreshold) => {
    return {
        selectedThreshold: Math.abs(threshold.value),
        operator: threshold.condition ?? MetricConditionOperator.GreaterThan,
        deltaMetricOperator: threshold.value < 0 ? DeltaMetricOperator.Below : DeltaMetricOperator.Above,
    };
};

export interface AddEditMonitoringMetricModalProps {
    availableMetricTypes: AppMonitoringHealthMetricType[];
    metricCategory: AppMonitoringHealthMetricCategory;
    metric?: AppMonitoringHealthMetric;
    open: boolean;
    onCancel: () => void;
    orgId: string;
    appId: string;
    currentReleaseStatsMap?: ExtendedStatsByType;
}

export const AddEditMonitoringMetricModal: React.FC<AddEditMonitoringMetricModalProps> = ({
    open,
    availableMetricTypes,
    onCancel,
    metric,
    appId,
    orgId,
    metricCategory,
    currentReleaseStatsMap,
}) => {
    const dispatch = useDispatch();
    const [form] = useForm<MonitoringMetricFormValues>();

    const [formIsValid, setFormIsValid] = React.useState(false);

    const updateMetricIsLoading = useIsLoading(AppSettingsUpdatable.UpdateMonitoringMetric, metric?.id);
    const updateMetricSuccess = useUpdated(AppSettingsUpdatable.UpdateMonitoringMetric, metric?.id);
    const updateMetricError = useError(AppSettingsUpdatable.UpdateMonitoringMetric, metric?.id);

    const addMetricIsLoading = useIsLoading(AppSettingsUpdatable.AddMonitoringMetric);
    const addMetricSuccess = useUpdated(AppSettingsUpdatable.AddMonitoringMetric);
    const addMetricError = useError(AppSettingsUpdatable.AddMonitoringMetric);

    const relevantIntegrations = useSelector((state: RootState) => {
        if (metric && metric.integrationInstallationIds) {
            return selectIntegrationsFromIntegrationInstallationIds(state, appId, metric.integrationInstallationIds);
        } else if (metricCategory) {
            // return integration for the capability associated with the category
            return selectIntegrationsForCapability(
                state,
                appId,
                requiredCapabilityForMonitoringMetricCategory(metricCategory),
            );
        }

        return null;
    });

    const addOrUpdateMonitoringMetric = (values: MonitoringMetricFormValues) => {
        const thresholds =
            values.thresholds?.map((rawThreshold) => {
                const signMultiplier =
                    (
                        isDeltaMetric(values.selectedMetricType) &&
                        rawThreshold.deltaMetricOperator === DeltaMetricOperator.Below
                    ) ?
                        -1
                    :   1;
                const threshold: AppMonitoringHealthMetricThreshold = {
                    value: rawThreshold.selectedThreshold * signMultiplier,
                    condition: rawThreshold.operator,
                };
                return threshold;
            }) ?? [];
        if (metric) {
            // editing existing metric
            const request: UpdateMonitoringMetricRequest = {
                orgId,
                appId,
                metricId: metric.id,
                thresholds,
                composedMetricIds: values.composedMetrics?.map((metric) => metric.id),
                composeMetricsUsingEquation: values.composeMetricsUsingEquation,
                hideOnRolloutView: values?.hideOnRolloutView ?? false,
                notifyOnApproachingThreshold: values?.notifyOnApproachingThreshold ?? false,
                description: values.metricDescription,
            };

            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateMonitoringMetric, request, metric.id));
        } else {
            const eventAndParam = values.selectedEventId ? separateEventAndParam(values.selectedEventId) : null;

            // adding new metric
            const request: AddMonitoringMetricRequest = {
                orgId,
                appId,
                category: metricCategory,
                thresholds,
                type: values.selectedMetricType,
                eventName: values.isComposedMetric ? values.eventName : (eventAndParam?.eventName ?? null),
                paramName: eventAndParam?.paramName ?? null,
                integrationInstallationId: values.selectedIntegrationInstallationId,
                composedMetricIds: values.composedMetrics?.map((metric) => metric.id),
                composeMetricsUsingEquation: values.composeMetricsUsingEquation,
                hideOnRolloutView: values?.hideOnRolloutView ?? false,
                notifyOnApproachingThreshold: values?.notifyOnApproachingThreshold ?? false,
                description: values.metricDescription,
            };

            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.AddMonitoringMetric, request));
        }
    };

    React.useEffect(() => {
        if (addMetricSuccess) {
            globalMessage.success('Health metric added!');

            onCancel();
        }
    }, [addMetricSuccess]);

    React.useEffect(() => {
        if (addMetricError) {
            globalMessage.error(addMetricError);
        }
    }, [addMetricError]);

    React.useEffect(() => {
        if (updateMetricError) {
            globalMessage.error(updateMetricError);
        }
    }, [updateMetricError]);

    React.useEffect(() => {
        if (updateMetricSuccess) {
            globalMessage.success('Health metric updated!');

            onCancel();
        }
    }, [updateMetricSuccess]);

    const onFieldsChange = () => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));
        setFormIsValid(errors.length === 0);
    };

    const isEdit = !!metric;

    const initialValues = React.useMemo(
        () => generateInitialValues({ metric, relevantIntegrations }),
        [metric, relevantIntegrations],
    );

    const onSubmit = () => {
        form.validateFields()
            .then((values) => {
                addOrUpdateMonitoringMetric(values);
            })
            .catch((e) => globalMessage.error(e.message));
    };

    const autoHaltUnstablePhasedReleases = useSelector((state: RootState) =>
        selectAutomationsByType(state, appId, AutomationType.AutoHaltUnstablePhasedReleases),
    );
    let foundMetricInAutoHaltPhasedReleases = false;

    if (metric) {
        foundMetricInAutoHaltPhasedReleases = autoHaltUnstablePhasedReleases.some((item) => {
            const automation = item as AutomationHaltUnstableRelease;
            return automation?.data?.monitoringConditions?.includes(metric.id);
        });
    }

    return (
        <Modal
            open={open}
            width={600}
            title={isEdit ? 'Edit health metric' : 'Add health metric'}
            okText={isEdit ? 'Save' : 'Add'}
            onOk={form.submit}
            okButtonProps={{ loading: updateMetricIsLoading || addMetricIsLoading, disabled: !formIsValid }}
            onCancel={onCancel}
            destroyOnClose
            afterOpenChange={() => {
                if (open && !isEdit) {
                    form.resetFields(['thresholds']);
                    form.resetFields(['composedMetrics']);
                }
            }}
            afterClose={() => form.resetFields()}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={() => {
                    onSubmit();
                }}
                preserve={false}
                initialValues={initialValues}
                onFieldsChange={onFieldsChange}
            >
                <MonitoringMetricFormFields
                    appId={appId}
                    isEdit={isEdit}
                    currentReleaseStatsMap={currentReleaseStatsMap}
                    metricCategory={metricCategory}
                    availableMetricTypes={availableMetricTypes}
                    integrations={relevantIntegrations}
                    isHideOnRolloutDisabled={foundMetricInAutoHaltPhasedReleases}
                />
            </Form>
        </Modal>
    );
};
